import React, { useContext, useEffect, useState, useMemo } from "react"
import { Input, Layout, Menu } from "antd"
import menuItems, { MenuItem } from "../../data/menuItems"
import {
  MENU_GROUP_DEFS,
  MENU_PREFS_CACHE_KEY,
  MenuPrefs,
  defaultGroupOf,
  readCachedPrefs,
} from "../../data/menuGroups"
import { request } from "../../utils/config"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import { TitleContext } from "../../contexts/TitleContext"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { SocketContext } from "../../contexts/SocketContext"
import hasPermission from "../../utils/hasPermission"
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint"
import { Badge } from "antd"
import MenuToggleButton from "../../components/button/MenuToggleButton"
import MobileToolBar from "../../components/navigation/MobileToolBar"
import useSettings from "../../hooks/useSettings"
import MenuReorderModal from "./MenuReorderModal"
import {
  Sliders,
  ShoppingCart,
  ShoppingBag,
  Package,
  DollarSign,
  Users as UsersIcon,
  Briefcase,
  FileText as FileTextIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  Monitor as MonitorIcon,
} from "react-feather"
// import { useSwipeable } from "react-swipeable";
import savLogo from "../../assets/sav-logo.png"   // Tog'a #41: SAV logosi (oltin, shaffof) — qora yon menyuda
import { applySupplierName } from "../../utils/supplierName"
import { useSectionNames, sectionGroupKey, sectionKeyOfRoute } from "../../utils/sectionNames"   // tog'a #3481
import { useQuery } from "@tanstack/react-query"
import { GET_PARTNER_APPS_NEW_COUNT } from "../../pages/partnerApplications/api"   // tog'a #3563 2-qism: menyu nishoni
import { useSectionBadges } from "../../components/notifications/useSectionBadges"   // tog'a #342 / #6024: bo'lim badge'lari
// Egasi №1/№4 (2026-08-31): guruh ta'riflari data/menuGroups.ts da (modal bilan
// bo'lishiladi). Bu — ADMIN STANDARTI; har foydalanuvchi MenuPrefs bilan o'ziga
// moslaydi: tartib, yashirish, elementni boshqa guruhga o'tkazish, yoki
// guruhlashni butunlay o'chirish. Ikonkalar shu yerda biriktiriladi.
const GROUP_ICONS: Record<string, any> = {
  "g-panel": MonitorIcon,
  "g-sales": ShoppingCart,
  "g-purch": ShoppingBag,
  "g-stock": Package,
  "g-fin": DollarSign,
  "g-partner": UsersIcon,
  "g-hr": Briefcase,
  "g-docs": FileTextIcon,
  "g-sys": SettingsIcon,
}
const MENU_GROUPS = MENU_GROUP_DEFS.map((g) => ({ ...g, icon: GROUP_ICONS[g.key] || SettingsIcon }))

export default function Sidebar() {
  const screens = useBreakpoint()
  const [collapsed, setCollapsed] = useState(false)
  const [reorderModalVisible, setReorderModalVisible] = useState(false)
  // Egasi (2026-09-02): «Menyuda search bo'lsin» — bo'sh bo'lmasa guruhlarsiz tekis natija ro'yxati
  const [menuQuery, setMenuQuery] = useState("")
  const [orderVersion, setOrderVersion] = useState(0)

  const history = useHistory()
  const { pathname } = useLocation()
  const { setTitle } = useContext(TitleContext)
  const { authState } = useContext(AuthContext)
  const { unreadMoneyChat, unreadOrderChat } = useContext(SocketContext)
  const [settings] = useSettings()
  const secNames = useSectionNames(!!authState?.user)   // tog'a #3481: tizim ichida o'zgartirilgan nomlar
  // tog'a #3563 2-qism: «Заявки партнёров» nishoni — yangi (status 0) arizalar soni; 60 s da bir marta, faqat ko'ra oladiganlarga
  const canSeePartnerApps =
    !!authState.isAuthenticated && Number((authState.user as any)?.tenantId || 1) <= 1 && hasPermission(authState.user, "/partnerApplications")
  const { data: newPartnerApps = 0, refetch: refetchPartnerApps } = useQuery(["PARTNER_APPS_NEW"], GET_PARTNER_APPS_NEW_COUNT, {
    enabled: canSeePartnerApps,
    refetchInterval: 60000,
    staleTime: 30000,
    retry: false,
  })
  const { lastMessage: wsLast } = useContext(SocketContext) as any
  useEffect(() => {
    if (canSeePartnerApps && wsLast?.nameFunction === "partner_application") refetchPartnerApps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsLast])
  // tog'a #342 / katalog #6024: 🔔 o'qilmaganlari bo'lim bo'yicha (joriy bo'limniki ochilganda o'qiladi)
  const secBadges = useSectionBadges(pathname)
  const isClient = authState.user?.userName === "Counterparty"
  const { t } = useTranslation(isClient ? "client" : "translation")
  // "Разделы системы" (Roles sahifasi) da o'zgartirilgan nom shu yerda ko'rinishi uchun —
  // avval doim statik tarjimadan olinardi, backend'dagi PermissionDetailVM.menuName e'tiborga olinmasdi.
  function getMenuLabel(menuItem: MenuItem) {
    const resourceName = (menuItem.link || menuItem.route)?.split("/")[1] || ""
    // tog'a #3481: «Разделы системы» da shu tizim uchun berilgan nom — hamma tilda ustun
    const maxsus = secNames[resourceName.toLowerCase()]
    if (maxsus) return maxsus
    const normResource =
      resourceName.toLowerCase() === "control" ? "recent" : resourceName.toLowerCase()
    const matchedPerm = (authState.user as any)?.permissions?.find(
      (perm: any) =>
        perm.moduleCode?.toLowerCase() === normResource ||
        perm.moduleCode?.toLowerCase() === resourceName.toLowerCase()
    )
    // Msg 270/436: UZ va EN rejimlarida TARJIMA ustuvor — DB'dagi menuName
    // faqat ruscha, aks holda menyu yarim-ruscha aralash chiqadi
    let appLang = "ru"
    try { appLang = localStorage.getItem("app_lang") || "ru" } catch {}
    const base =
      appLang === "uz" || appLang === "en"
        ? t(menuItem.title)
        : matchedPerm?.menuName || t(menuItem.title)
    return applySupplierName(menuItem.title, base, authState.user)
  }

  function handleMenuItemClick(menuItem: MenuItem) {
    setTitle(menuItem.seoTitle, secNames[sectionKeyOfRoute(menuItem.link || menuItem.route)])
    !screens.lg && !collapsed && setCollapsed(true)
    history.push(menuItem.link || menuItem.route)
  }

  useEffect(() => {
    setCollapsed(!screens.xl)
  }, [screens])

  useEffect(() => {
    const item = menuItems.find(
      (x) =>
        pathname === x.link ||
        pathname === x.route ||
        pathname.startsWith("/" + x.title)
    )
    setTitle(item?.seoTitle || "", item ? secNames[sectionKeyOfRoute(item.link || item.route)] : undefined)   // tog'a #3481
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, setTitle, secNames])

  const filteredMenuItems = useMemo(() => {
    const filtered = menuItems.filter((x) => {
      const u = authState?.user as any;
      // tog'a 1797 (2026-09-13, Boshliq talabi): «Главная панель» (route «/») menyuda HECH KIMGA
      // ko'rinmaydi — super-admin/admin uchun ham («hech qayerda hatto adminda ham chiqmasin»).
      // «Глобальная панель» (/globalPanel) uning o'rnini bosadi; «/» ochilsa pages/home darhol yo'naltiradi.
      if (x.route === "/") return false;
      const isCounterparty = u?.userName === "Counterparty" || u?.mainRoles?.resourceName === "counterparty" || u?.roleName === "counterparty" || (u?.counterpartyId != null && u?.counterpartyId > 0 && u?.managerId == null);
      
      const resourceName = (x.link || x.route)?.split("/")[1] || "";
      const normResource = resourceName.toLowerCase() === "control" ? "recent" : resourceName.toLowerCase();
      
      const isDataAdminUser = u?.mainRoles?.resourceName?.toLowerCase() === "dataadmin" || u?.roleName?.toLowerCase() === "dataadmin";
      if (isDataAdminUser) {
        return normResource === "dataadmin";
      }

      // tog'a #1940 kampaniyasi (2026-09-14): «Резервные копии» / «Администрирование данных» — backend guardlari bilan bir xil (Sidebar).
      // Pastdagi umumiy «qator bo'lsa showInMenu && canRead» qoidasi rol matritsasida backups qatori bor menejerga o'lik bandni ko'rsatardi
      // (backend faqat tenant 1 super-admin / Administrator / Moderator / DataAdmin roliga ruxsat beradi) — hasPermission shu qoidani biladi.
      if (normResource === "backups" || normResource === "dataadmin" || normResource === "legalpages") {
        return hasPermission(authState.user, x.route);
      }

      // 177-xabar: "Кабинет завода" alohida bo'lim FAQAT zavod userlari uchun —
      // kompaniya menyusida ko'rinmaydi (jarayon "Закупка завода" ichida)
      if (normResource === "factoryshipments") {
        const isFactory = u?.userName === "Factory" || (u?.factoryId != null && u?.factoryId > 0 && u?.managerId == null && u?.counterpartyId == null);
        return isFactory;
      }

      // 234-xabar: "Кабинет магазина" ham faqat do'kon userlari uchun
      if (normResource === "shopcabinet") {
        // Boshliq 5-qaror (2026-09-14): magazin TIZIMI admini (o'z tenanti) kabinet emas — ShopId saqlangan bo'lsa ham band ko'rinmaydi
        // (hasPermission.isShopUser dagi audit F1 istisnosi bilan bir xil; aks holda bosilsa /globalPanel ga qaytardi)
        const shopSystemAdmin = Number(u?.tenantId || 1) > 1 && (String(u?.userType || "").toLowerCase() === "admin" || (u as any)?.tenantType === "shop_owner");
        const isShop = !shopSystemAdmin && (u?.userName === "Shop" || (u?.shopId != null && u?.shopId > 0 && u?.managerId == null && u?.counterpartyId == null && u?.factoryId == null));
        return isShop;
      }

      // 236-xabarda yashirilgan edi; egasi 2026-08-31: Магазины QAYTDI (Партнёры
      // guruhida) — magazinlar alohida yuritiladi, huquq matritsa bo'yicha.

      // 298-xabar: "customers bu razdelni toliq olib tashla kk emas" —
      // SAV/kontragent menyusidan olib tashlangan. S20: MAGAZIN tizimida esa
      // bu asosiy bo'lim (klient bazasi) — faqat o'sha yerda qaytadi.
      if (normResource === "customers") {
        return u?.tenantType === "shop_owner"
      }

      // Egasi 2026-08-31 (№3): Менеджеры Персонал guruhida KO'RINSIN — menejer
      // tizimni boshqarishda yordam beradigan xodim. Huquq matritsa bo'yicha.

      // S16: tenant turi bo'yicha profil.
      // - "Заказ поставщику" FAQAT kontragent/magazin tizimlarida (tenantId > 1)
      // - "Закупка завода"/"Заводы"/"Портал заводов" kontragent tizimida KERAK EMAS —
      //   ular SAVdan zakaz orqali oladi (glavniyda hammasi avvalgidek qoladi)
      const tenantId = Number(u?.tenantId || 1);
      // Egasi (2026-09-01): bu ikki punkt endi katalogda bor — rol matritsasida
      // QATORI bo'lsa o'sha hal qiladi (olib tashlash/qo'shish ishlaydi),
      // qatori yo'q rol uchun eski default saqlanadi.
      const specialRow = (code: string) =>
        (u?.permissions || []).find(
          (perm: any) => (perm.moduleCode || "").toLowerCase() === code
        );
      // Audit R1/R2/R7 (2026-09-03): KABINET-login (kontragent/magazin/zavod) uchun menyu FAIL-CLOSED bo'lishi kerak —
      // serverda modul yopilgan bo'lsa MyPermissions'dan chiqib ketadi, menyu esa «topilmadi = ko'rsat» deb 403 beradigan
      // bo'limlarni chiqarib turardi. Tizim ADMINI (o'z tenanti bor) kabinet emas — unga eski default saqlanadi.
      const isCabinetUser =
        !(tenantId > 1 && (String(u?.userType || "").toLowerCase() === "admin" || (u as any)?.tenantType === "shop_owner")) &&   // audit F1
        (Number(u?.counterpartyId || 0) > 0 || Number(u?.shopId || 0) > 0 || Number(u?.factoryId || 0) > 0);
      const rowOrDefault = (code: string) => {
        const p = specialRow(code);
        if (p) return !!p.showInMenu && !!p.canRead;
        return !isCabinetUser;   // kabinetga yopiq, boshqalarga eski default
      };
      // talabnoma §2.3 (2026-09-06): magazin tizimida «Заказы» (SAV moduli — hamkorlardan kelgan zakazlar) ko'rinmaydi;
      // magazin oqimi: «Заказ поставщику» (/orderToMain) → SAV tasdiqlaydi → «Закупка» (/purchases, GS-)
      if (normResource === "orders" && u?.tenantType === "shop_owner") return false;
      if (normResource === "ordertomain") {
        if (tenantId <= 1) return false;
        return rowOrDefault("ordertomain");
      }
      // S22: Global panel — default hammaga ochiq (ma'lumot tenant/scope-filtrli)
      if (normResource === "globalpanel") {
        return rowOrDefault("globalpanel");
      }
      // S18: "Системы партнёров" paneli — faqat GLAVNIY admin (tenant 1 + admin roli)
      if (normResource === "tenants") {
        const isMainAdminU =
          tenantId === 1 &&
          (u?.role === 1 || u?.roleId === 1 || u?.userName === "Administrator" ||
           u?.mainRoles?.resourceName === "super-admin" || u?.roleName === "super-admin");
        return !!isMainAdminU;
      }
      // tog'a #3563 2-qism: «Заявки партнёров» — faqat glavniy tizim (tenant 1); super-admin doim, qolganlar rol matritsasi qatori bilan
      // (yangi huquq, sukut — faqat super-admin: qatori yo'q xodimga yopiq)
      if (normResource === "partnerapplications") {
        if (tenantId > 1) return false;
        const superU = u?.userName === "Administrator" || u?.userName === "Moderator" || u?.mainRoles?.resourceName === "super-admin" ||
          u?.roleName === "super-admin" || u?.role === 1 || u?.roleId === 1;
        if (superU) return true;
        const p = specialRow("partnerapplications");
        return p ? !!p.showInMenu && !!p.canRead : hasPermission(authState.user, x.route);
      }
      if (tenantId > 1 && ["factorypurchases", "factories", "factoryshipments"].includes(normResource)) {
        return false;
      }
      // Egasi (2026-08-31): rol-i-prava MARKAZLASHGAN — tenant (kontragent/magazin)
      // tizimlarida «Роли и права» bo'lmaydi; hammasini glavniy admin boshqaradi
      // (Системы партнёров -> 🛡). Backend'da ham yozish 403 bilan yopilgan.
      // (2026-09-02) faqat haqiqiy hamkor-tizim belgilari; begona/eskirgan tenantType qiymati yashirmasin
      if (normResource === "roles" && (tenantId > 1 || u?.tenantType === "shop_owner" || u?.tenantType === "counterparty")) {
        return false;
      }
      // S20: MAGAZIN tizimi profili — do'kon kontragent yuritmaydi, o'z do'konlari
      // ham bo'lmaydi. Uning o'rniga "Клиенты" (yuqorida ochildi) va bonuslar.
      // Boshliq (2026-09-03, S2): magazin tizimida «Магазины» ham yo'q — faqat klient bazasi
      // Boshliq (2026-09-04, S3/1088): magazin TIZIMIDA «Кабинет магазина» KERAK (o'z ombori bo'yicha) — faqat
      // «Контрагенты» va «Магазины» yo'q. Kabinet punkti rol qatori bilan boshqariladi (qatori bo'lsa o'sha, bo'lmasa ko'rinadi).
      if (u?.tenantType === "shop_owner" && ["counterparties", "shops"].includes(normResource)) {
        return false;
      }
      if (u?.tenantType === "shop_owner" && normResource === "shopcabinet") {
        return rowOrDefault("shopcabinet");
      }

      // 239-xabar: Декларации + Контракты endi bitta "ДЕКЛАРАНТ" bo'limida —
      // eski ikkita punkt menyudan yashirin (routelari ishlayveradi)
      // Yagona chat (egasi 2026-09-02): chat menyuda emas — sarlavhadagi tugma; eski ikki band yo'q
      if (normResource === "chat" || normResource === "moneycomments" || normResource === "ordercomments") {
        return false;
      }
      if (normResource === "declarations" || normResource === "contracts") {
        return false;
      }
      if (normResource === "declarant") {
        const isAdminU =
          u?.userName === "Administrator" ||
          u?.userName === "Moderator" ||
          u?.mainRoles?.resourceName === "super-admin" ||
          u?.roleName === "super-admin" ||
          u?.role === 1 ||
          u?.roleId === 1;
        const hasDeclPerm = (u?.permissions || []).some(
          (p: any) =>
            ["declarations", "contracts"].includes((p.moduleCode || "").toLowerCase()) &&
            p.showInMenu
        );
        return isAdminU || hasDeclPerm;
      }

      // S5: "Пользователи" (yagona foydalanuvchilar) — admin yoki "managers"
      // razdeli berilgan menejerlar ko'radi (bo'lim o'sha razdelning davomchisi)
      if (normResource === "usersdirectory") {
        // Boshliq (2026-09-04): «Пользователи» ROL bilan boshqariladi — glavniy super-admin doim; qolganlar uchun
        // «managers» qatori (canRead && showInMenu). Hamkor/magazin tizimi admini ham shu qatorga bo'ysunadi
        // (backend endi tenant super-admin uchun matritsa qatorlarini qo'llaydi).
        const isMainSuper = tenantId <= 1 && (u?.userName === "Administrator" || u?.userName === "Moderator" ||
          u?.mainRoles?.resourceName === "super-admin" || u?.roleName === "super-admin" || u?.role === 1 || u?.roleId === 1);
        // Boshliq (2026-09-04): «Пользователи» = katalogdagi «users» qatori (rol-huquqda «Пользователи»); yo'q bo'lsa «managers»
        const uRow = specialRow("users");
        const mRow = specialRow("managers");
        if (isMainSuper) return true;
        if (uRow) return !!uRow.showInMenu && !!uRow.canRead;
        if (mRow) return !!mRow.showInMenu && !!mRow.canRead;
        return !isCabinetUser && tenantId <= 1;
      }

      if (normResource === "dataadmin") {
        return hasPermission(authState.user, x.route);
      }
      
      const isAdmin =
        u?.userName === "Administrator" ||
        u?.userName === "Moderator" ||
        u?.mainRoles?.resourceName === "super-admin" ||
        u?.roleName === "super-admin" ||
        u?.role === 1 ||
        u?.roleId === 1;
      if (isAdmin && normResource !== "dataadmin") {
        return true;
      }
      // tog'a 1797: «/» (Главная панель) endi filtr boshida butunlay yashirilgan — eski «home» qatori
      // tekshiruvi (tog'a 1319) shu sababli olib tashlandi.

      // 2026-08-30: kontragent ham dinamik huquqlardan foydalanadi — admin
      // "Роли и права"da belgilagani menyuda aks etadi (qator yo'q bo'lsa
      // pastdagi hasPermission kontragent uchun yopadi, tasklardan tashqari).
      if (authState?.user?.permissions && authState.user.permissions.length > 0 && resourceName !== "") {
        const p = authState.user.permissions.find((perm: any) => (perm.moduleCode?.toLowerCase() === normResource || perm.moduleCode?.toLowerCase() === resourceName.toLowerCase()));
        // Farhod #551 (2026-09-01): bo'lim menyuda ko'rinib, bosilganda «Отказано
        // в доступе» chiqardi — chunki matritsada «Menyuda ko'rsatish» yoqilgan,
        // «Ko'rish» esa yoqilmagan bo'lsa menyu baribir ko'rsatardi.
        // Endi menyuda FAQAT ochib bo'ladigan bo'limlar turadi.
        if (p) return !!p.showInMenu && !!p.canRead;
      }
      return hasPermission(authState.user, x.route);
    })

    // Egasi №4: shaxsiy sozlama (server MenuPrefs keshi) — yashirish + tartib.
    //
    // Egasi (2026-09-01): «glavnyy panel ikkita bo'lib turibdi… menyuda
    // yashirishni qildim, ammo yashirinmadi. Globalniy panel qolishi kerak,
    // glavnyy panelni yashirib turish kerak — kimga kerak bo'lsa o'zi ochadi».
    // Sabab shu yerda edi: «home» qatoriga MAXSUS istisno qo'yilgan ekan
    // (`k === "/" || m.title === "home"` → hech qachon yashirilmasin).
    // Istisno olib tashlandi. Xavfsiz: yashirish faqat MENYUGA ta'sir qiladi,
    // «/» marshruti va unga kirish huquqi o'z joyida qoladi — foydalanuvchi
    // xohlasa manzil bo'yicha yoki sozlamadan qaytarib ochadi.
    const prefs: MenuPrefs = readCachedPrefs() || {}
    let list = filtered
    if (prefs.hidden && prefs.hidden.length > 0) {
      const hiddenSet = new Set(prefs.hidden)
      // Egasi (2026-09-02): «Роли и права» shaxsiy menyu sozlamasi bilan yashirib bo'lmaydi (admin uni yo'qotib qo'ymasin)
      list = list.filter((m) => !hiddenSet.has(m.link || m.route) || (m.route || m.link) === "/roles")
    }
    const order: string[] =
      prefs.order && prefs.order.length > 0
        ? prefs.order
        : (() => {
            try {
              return JSON.parse(localStorage.getItem("menu_custom_order") || "[]")
            } catch {
              return []
            }
          })()
    if (order.length > 0) {
      list = [...list].sort((a, b) => {
        const indexA = order.indexOf(a.link || a.route)
        const indexB = order.indexOf(b.link || b.route)
        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return 0
      })
    }
    return list
  }, [authState.user, orderVersion])

  // Shaxsiy sozlamani serverdan bir marta tortib keshga qo'yamiz (har qurilmada
  // bir xil bo'lishi uchun); kelgach menyu qayta chiziladi.
  useEffect(() => {
    if (!authState.isAuthenticated) return
    request({ url: "AccountAPI/MenuPrefs" })
      .then((r: any) => {
        const raw = r?.data?.prefs
        if (raw) localStorage.setItem(MENU_PREFS_CACHE_KEY, raw)
        else localStorage.removeItem(MENU_PREFS_CACHE_KEY)
        setOrderVersion((v) => v + 1)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated])

  return (
    <>
      <Layout.Sider
        className={`custom-sider ${settings.toolbar ? "has-toolbar" : ""} ${
          !screens.lg ? "custom-sider-mobile" : ""
        } ${collapsed ? "custom-collapsed" : ""}`}
        collapsible
        collapsed={screens.lg ? collapsed : false}
        onCollapse={setCollapsed}
        {...(!screens.lg && { trigger: null })}
        collapsedWidth={!screens.lg ? 0 : 80}
        width={215}
      >
        <div className="logo zd-brand">
          <img alt="SAV" className="zd-brand-logo" src={savLogo} />
          <span className="zd-brand-tag logo-full">ERP</span>
        </div>
        {!collapsed && (authState?.user?.userName || authState?.user?.email) && (
          <div className="zd-sider-user">
            {authState.user.userName || authState.user.email}
          </div>
        )}
        {!collapsed && (
          <div className="menu-search">
            <Input allowClear size="small" placeholder={t("menuSearch")} value={menuQuery} onChange={(e) => setMenuQuery(e.target.value)} />
          </div>
        )}
        <Menu
          className="MenuSaidbar_r"
          inlineIndent={0}
          key={pathname}
          selectedKeys={["/" + pathname.split("/")[1]]}
          // Joriy sahifa qaysi guruhda bo'lsa — o'sha ochiq holda chiqadi
          // (foydalanuvchining shaxsiy guruh-o'zgartirishlari inobatga olinadi)
          defaultOpenKeys={(() => {
            const cur = "/" + pathname.split("/")[1]
            const found = menuItems.find((m) => (m.link || m.route) === cur)
            if (!found) return []
            const prefs: MenuPrefs = readCachedPrefs() || {}
            if (prefs.flat) return []
            const k = found.link || found.route
            const customKeys = new Set((prefs.customGroups || []).map((g) => g.key))
            let gk =
              prefs.groups && k in prefs.groups
                ? prefs.groups[k]
                : defaultGroupOf(found.title)
            if (gk && !customKeys.has(gk) && !MENU_GROUP_DEFS.some((d) => d.key === gk))
              gk = defaultGroupOf(found.title)
            return gk ? [gk] : []
          })()}
          mode="inline"
          theme="light"
        >
          {(() => {
            const renderItem = (menuItem: MenuItem) => (
              <Menu.Item
                key={menuItem.link || menuItem.route}
                disabled={!menuItem.component}
                onClick={() => handleMenuItemClick(menuItem)}
                icon={
                  menuItem.title === "moneyComments" ? (
                    <Badge dot={unreadMoneyChat && !secBadges[menuItem.link || menuItem.route]} count={secBadges[menuItem.link || menuItem.route] || 0} size="small" offset={[-10, 5]}>
                      <menuItem.icon />
                    </Badge>
                  ) : menuItem.title === "orders" ? (
                    // tog'a #342 / #6024: o'qilmagan bildirishnoma bo'lsa — soni, aks holda chat nuqtasi
                    <Badge dot={unreadOrderChat && !secBadges[menuItem.link || menuItem.route]} count={secBadges[menuItem.link || menuItem.route] || 0} size="small" offset={[-10, 5]}>
                      <menuItem.icon />
                    </Badge>
                  ) : menuItem.title === "partnerApplications" && newPartnerApps > 0 ? (
                    // tog'a #3563 2-qism: yangi arizalar soni
                    <Badge count={newPartnerApps} size="small" overflowCount={99} offset={[-6, 2]}>
                      <menuItem.icon />
                    </Badge>
                  ) : (secBadges[menuItem.link || menuItem.route] || 0) > 0 ? (
                    // tog'a #342 / #6024: o'qilmagan bildirishnomalar soni
                    <Badge count={secBadges[menuItem.link || menuItem.route]} size="small" overflowCount={99} offset={[-6, 2]}>
                      <menuItem.icon />
                    </Badge>
                  ) : (
                    <menuItem.icon />
                  )
                }
              >
                {getMenuLabel(menuItem)}
              </Menu.Item>
            )

            // Guruhlash (№1+№4): foydalanuvchi TEKIS rejimni tanlagan bo'lsa —
            // hammasi yakka; aks holda har element o'zi tanlagan guruhga (yoki
            // standart guruhiga) tushadi. null-guruh = yuqorida yakka.
            const q = menuQuery.trim().toLowerCase()
            if (q) {
              const hit = filteredMenuItems.filter((m) =>
                [getMenuLabel(m), t(m.title), m.title, (m as any).seoTitle, m.link || m.route].filter(Boolean).some((x) => String(x).toLowerCase().includes(q))
              )
              return <>{hit.map(renderItem)}</>
            }
            const prefs: MenuPrefs = readCachedPrefs() || {}
            // Egasi №2: foydalanuvchi yaratgan guruhlar ham chiziladi (📁 belgisi)
            const customDefs = (prefs.customGroups || []).map((g) => ({
              key: g.key,
              label: g.label,
              items: [] as string[],
              icon: FolderIcon,
              // Egasi (2-to'lqin): foydalanuvchi tanlagan emoji-ikon
              emoji: g.icon,
            }))
            const allGroups = [...MENU_GROUPS, ...customDefs]
            const validKeys = new Set(allGroups.map((g) => g.key))
            const effectiveGroupOf = (m: MenuItem): string | null => {
              if (prefs.flat) return null
              const k = m.link || m.route
              if (prefs.groups && k in prefs.groups) {
                const v = prefs.groups[k]
                // o'chirilgan (endi mavjud emas) guruhga ishora — standartga qaytadi
                if (v !== null && !validKeys.has(v)) return defaultGroupOf(m.title)
                return v
              }
              return defaultGroupOf(m.title)
            }
            const singles = filteredMenuItems.filter((m) => effectiveGroupOf(m) === null)
            let groups = allGroups
              .map((g) => ({
                ...g,
                children: filteredMenuItems.filter((m) => effectiveGroupOf(m) === g.key),
              }))
              .filter((g) => g.children.length > 0)
            // Egasi (2-to'lqin) №2: guruhlar TARTIBI — foydalanuvchi belgilagan
            // ketma-ketlik; ro'yxatda yo'q guruhlar standart o'rnida qoladi
            const gOrder = prefs.groupOrder || []
            if (gOrder.length) {
              groups = [...groups].sort((a, b) => {
                const ia = gOrder.indexOf(a.key)
                const ib = gOrder.indexOf(b.key)
                if (ia !== -1 && ib !== -1) return ia - ib
                if (ia !== -1) return -1
                if (ib !== -1) return 1
                return 0
              })
            }
            // Egasi (2-to'lqin) №3: yashirilgan guruhlar butunlay chiqmaydi —
            // ichidagi bo'limlar ham; faqat «Главная» (/) yakka bo'lib qoladi
            const hiddenG = new Set(prefs.hiddenGroups || [])
            const rescued: MenuItem[] = []
            groups = groups.filter((g) => {
              if (!hiddenG.has(g.key)) return true
              const home = g.children.find((m) => (m.link || m.route) === "/")
              if (home) rescued.push(home)
              return false
            })
            return (
              <>
                {[...singles, ...rescued].map(renderItem)}
                {groups.map((g) => {
                  const GIcon = g.icon
                  const hasBadge =
                    (unreadMoneyChat && g.items.includes("moneyComments")) ||
                    (unreadOrderChat && g.items.includes("orders")) ||
                    (newPartnerApps > 0 && g.items.includes("partnerApplications")) ||   // tog'a #3563 2-qism
                    g.children.some((m) => (secBadges[m.link || m.route] || 0) > 0)   // tog'a #342 / #6024
                  const gLabel = secNames[sectionGroupKey(g.key)] || ((g as any).labelKey ? t((g as any).labelKey) : g.label)   // tog'a #3481
                  return (
                    <Menu.SubMenu
                      key={g.key}
                      title={gLabel}
                      icon={(() => {
                        // Emoji tanlangan bo'lsa — o'sha, aks holda odatiy ikon
                        const base = (g as any).emoji ? (
                          <span style={{ fontSize: 14, lineHeight: 1 }}>{(g as any).emoji}</span>
                        ) : (
                          <GIcon />
                        )
                        return hasBadge ? (
                          <Badge dot offset={[-10, 5]}>
                            {base}
                          </Badge>
                        ) : (
                          base
                        )
                      })()}
                    >
                      {g.children.map(renderItem)}
                    </Menu.SubMenu>
                  )
                })}
              </>
            )
          })()}
        </Menu>
        {!collapsed && (
          <div style={{ padding: "10px 14px", borderTop: "1px solid rgba(0,0,0,0.06)", textAlign: "center" }}>
            {/* Egasi (2026-08-31): til almashtirgich HEADERGA ko'chdi
                (bildirishnomalar yonida, RU/UZ/EN) — bu yerdan olib tashlandi */}
            <button
              className="menu-reorder-btn"
              onClick={() => setReorderModalVisible(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#f0f5ff",
                border: "1px solid #adc6ff",
                color: "#1d39c4",
                borderRadius: "6px",
                padding: "4px 10px",
                fontSize: "12px",
                cursor: "pointer",
                fontWeight: 600,
                width: "100%",
                justifyContent: "center",
              }}
            >
              <Sliders size={13} />
              <span>{t("menuReorder")}</span>
            </button>
          </div>
        )}
      </Layout.Sider>
      <MenuReorderModal
        visible={reorderModalVisible}
        onClose={() => setReorderModalVisible(false)}
        onOrderSaved={() => setOrderVersion((v) => v + 1)}
      />
      {/* Boshliq (2026-09-04): planshet/telefonda menyu tugmasi — sarlavhaning chap yuqori burchagida DOIMIY 44px tugma.
          Sudraladigan suzuvchi tugma olib tashlandi: ekran chetida iOS «orqaga» imosi bilan to'qnashardi, sudrash dastagi
          bilan adashtirardi, menyu bandlari ustida turardi; landshaftda esa pastdagi trigger 100vh tufayli Safari paneli
          ostida qolardi. Menyu ochiq bo'lsa ortida qoraytirgich — tashqariga bosilsa yopiladi. */}
      {!screens.xl && (
        <MenuToggleButton open={!collapsed} mobile={!screens.lg} onClick={() => setCollapsed(!collapsed)} />
      )}
      {!screens.lg && !collapsed && (
        <div className="sider-backdrop" onClick={() => setCollapsed(true)} aria-hidden="true" />
      )}
      {!screens.lg && settings.toolbar && (
        <MobileToolBar onMenuButtonClicked={() => setCollapsed(!collapsed)} />
      )}
    </>
  )
}
